<template>
  <feature
    name="MANAGED_CAMPAIGNS"
  >
    <b-btn
      variant="secondary"
      size="sm"
      v-b-modal.manageDealsModal
      data-test="manage-deals-button"
      :disabled="isCampaignReadOnly"
    >
      Manage deals
    </b-btn>
    <b-modal
      id="manageDealsModal"
      ref="manageDealsModal"
      title="Manage Publisher Deals"
      size="xl"
      ok-only
      ok-title="Save"
      ok-variant="primary"
      @show="resetNewDeals"
      @hidden="resetNewDeals"
      @ok="handleOk"
      data-test="manage-deals-modal"
    >
      <div
        v-if="isLoading"
        class="h-100 align-items-center d-flex justify-content-center card-body"
      >
        <div class="loader mx-auto" />
      </div>
      <template v-else>
        <div class="row">
          <div class="table-responsive">
            <b-table
              class="card-table table-vcenter text-nowrap"
              tbody-tr-class="table-row"
              :bordered="false"
              :outlined="false"
              :hover="true"
              :items="dealList"
              :fields="getHeaders(true)"
              show-empty
              empty-text="No deal overrides added"
              sort-by="publisher"
              data-test="manage-deals-overrides-table"
            >
              <template
                v-slot:cell(publisherId)="{item}"
              >
                {{ getPublisherDetails(item.publisherId).name }}
              </template>
              <template
                v-slot:cell(creativeTemplateName)="{item}"
              >
                {{ getCreativeSet(item.creativeTemplateId).name }}
              </template>
              <template
                v-slot:cell(action)="{index}"
              >
                <i
                  class="fal fa-trash-can float-right remove-btn"
                  @click="removeDeal(index)"
                  data-test="manage-deals-delete-button"
                />
              </template>
            </b-table>
          </div>
        </div>
        <b-alert
          show
          variant="warning"
          v-if="showWarning"
        >
          Please note, all deals will be added when the campaign is created.
        </b-alert>
        <div class="form-label">
          Add Deal Override
        </div>
        <div class="table-responsive">
          <b-table
            class="card-table table-vcenter text-nowrap"
            tbody-tr-class="table-row"
            :bordered="false"
            :outlined="false"
            :items="newDealList"
            :fields="getHeaders()"
            sort-by="publisher"
          >
            <template #table-colgroup="scope">
              <col
                v-for="field in scope.fields"
                :key="field.key"
                :style="{ width: field.width ? field.width : 'auto' }"
              >
            </template>
            <template
              v-slot:cell(publisherId)="{item}"
            >
              <b-form-select
                v-model="item.publisherId"
                :options="getPublisherList"
                data-test="manage-deals-publisher-select"
              />
            </template>
            <template
              v-slot:cell(creativeTemplateName)="{item}"
            >
              <b-form-select
                v-model="item.creativeTemplateId"
                :options="getCreativeTemplateAsList"
                data-test="manage-deals-template-id-select"
              />
            </template>
            <template
              v-slot:cell(dealIdString)="{item}"
            >
              <input
                type="text"
                class="form-control"
                v-model="item.dealIdString"
                data-test="manage-deals-deal-id-input"
              >
            </template>
          </b-table>
        </div>
        <div
          class="d-flex"
        >
          <b-btn
            class="ml-auto"
            size="sm"
            variant="secondary"
            @click="onUpdateDeals"
            data-test="manage-deals-add-deal-button"
          >
            Add to list
          </b-btn>
        </div>
      </template>
    </b-modal>
  </feature>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';


export default {
  name: 'ManageDeals',
  components: {
  },
  props: {
    publishers: {
      type: Array,
      default: () => [],
    },
    creativeTemplateIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      dealList: [],
      newDealList: [],
      dealSet: {
        publisherId: 0,
        creativeId: 0,
        creativeTemplateId: 0,
        dealIdString: '',
      },
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'dealOverrides',
    ]),
    ...mapState('content', [
      'creative',
      'creatives',
    ]),
    ...mapState('templates', [
      'availableTemplates',
      'selectedTemplates',
    ]),
    ...mapGetters('campaigns', {
      isCampaignReadOnly: campaignTypes.GET_IS_CAMPAIGN_READONLY,
    }),
    getCreativeTemplateAsList() {
      if (this.availableTemplates.length === 0) return [];
      const templateSet = this.getTemplateSet(this.creativeTemplateIds);
      if (!templateSet) return [];
      return templateSet.map((template) => ({
        value: template.id,
        text: template.name,
      }));
    },
    isNewCampaign() {
      return (!this.campaign.id);
    },
    isListNotEmpty() {
      return (this.dealList.length !== 0);
    },
    showWarning() {
      return this.isNewCampaign && this.isListNotEmpty;
    },
    getPublisherList() {
      return this.publishers.map((publisher) => ({ value: publisher.id, text: publisher.name }));
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.FETCH_DEAL_OVERRIDES,
      campaignTypes.UPDATE_DEAL_OVERRIDES,
      campaignTypes.SUBMIT_DEAL_OVERRIDES,
    ]),
    ...mapActions('content', [
      contentTypes.GET_TEMPLATE_BY_ID,
    ]),
    getHeaders(actionCol) {
      const headerList = [
        {
          key: 'publisherId',
          label: 'Publisher',
          tdClass: 'text-wrap',
        },
        {
          key: 'creativeTemplateName',
          label: 'Creative',
          tdClass: 'text-wrap',
        },
        {
          key: 'dealIdString',
          label: 'Deal ID',
          width: '30%',
        },
      ];
      if (actionCol) {
        headerList.push({
          key: 'action',
          label: '',
          width: '10%',
        });
      }
      return headerList;
    },
    addRow() {
      this.newDealList.push({ ...this.dealSet });
    },
    removeDeal(index) {
      this.dealList.splice(index, 1);
    },
    resetNewDeals() {
      this.newDealList = [];
      this.addRow();
    },
    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },
    handleSubmit() {
      const newDealList = this.dealList
        .map((deal) => ({
          publisherId: deal.publisherId,
          creativeTemplateId: deal.creativeTemplateId,
          creativeId: deal.creativeId ? deal.creativeId : this.getCreativeId(deal.creativeTemplateId),
          dealIdString: deal.dealIdString.trim(),
        }));
      this.UPDATE_DEAL_OVERRIDES(newDealList);
      this.$nextTick(() => {
        if (!this.isNewCampaign) {
          this.SUBMIT_DEAL_OVERRIDES();
        }
        this.$bvModal.hide('manageDealsModal');
      });
    },
    getCreativeId(creativeTemplateId) {
      if (!creativeTemplateId) return 0;
      if (!this.creatives.length) return this.creative.id;
      const creative = this.creatives.find((c) => (c.creativeTemplateId === creativeTemplateId));
      return creative ? creative.id : 0;
    },
    removeEmptyObjects(obj) {
      return obj.filter((o) => (o.publisherId && o.creativeTemplateId && o.dealIdString));
    },
    onUpdateDeals() {
      this.newDealList = this.removeEmptyObjects(this.newDealList);
      if (this.newDealList.length === 0) return this.addRow();
      this.dealList = [
        ...this.dealList,
        ...this.newDealList,
      ].slice()
        .reverse()
        .filter((v, i, a) => {
          const uid = `${v.publisherId}${v.creativeTemplateId}`;
          const alreadyExists = (a.findIndex((t) => (`${t.publisherId}${t.creativeTemplateId}` === uid)) === i);
          return alreadyExists;
        })
        .reverse();
      this.resetNewDeals();
    },
    getPublisherDetails(id) {
      return this.publishers.find((publisher) => Number(publisher.id) === Number(id));
    },
    getCreativeSet(creativeTemplateId) {
      if (creativeTemplateId) {
        return { name: this.getTemplate(creativeTemplateId).name };
      }
      if (this.isNewCampaign && this.creative.id === 0) {
        return { name: this.getCreativeTemplateAsList[0].text };
      }
      const templateSet = this.getTemplate(this.creativeTemplateIds);
      return {
        ...templateSet,
      };
    },
    getTemplate(id) {
      return this.availableTemplates.find((template) => template.id === id);
    },
    getTemplateSet(ids) {
      return this.availableTemplates.filter((template) => ids.includes(template.id));
    },
    fetchDealOverrides() {
      if (!this.isNewCampaign) {
        this.isLoading = true;
        this.FETCH_DEAL_OVERRIDES()
          .then(() => {
            this.dealOverrides.map((deal) => {
              const creative = this.creatives.length ? this.creatives.find((c) => (c.id === deal.creativeId)) : this.creative;
              deal.creativeTemplateId = creative ? creative.creativeTemplateId : 0;
              return deal;
            });
            this.dealList = this.dealOverrides;
            this.isLoading = false;
          });
      }
    },
  },
  mounted() {
    this.fetchDealOverrides();
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === 'manageDealsModal') {
        if (this.dealList.length === 0) this.dealList === this.dealOverrides;
      }
    });
  },
  created() {
  },
  destroyed() {
    this.$root.$off('bv::modal::show');
  },
};
</script>
<style>
.remove-btn{
  cursor: pointer;
}
</style>
